import {AxiosError} from "axios";
import {ApiConfig} from 'components/src/api/admin/ApiConfig'
import {
    PropertyApi,
    UserPropertiesDetails,
    UserPropertyDescriptorDetails,
    type UserPropertyDetails
} from 'components/src/api/admin/generated';
import {useSpinnerStatusContext} from "components/src/hook/UseSpinnerStatusContext";
import {logger} from "components/src/utils/Logger";
import {createContext, useCallback, useEffect, useMemo, useState} from 'react'

export interface AvailableProperties {
    globalProperties : UserPropertiesDetails[],
    domainProperties : UserPropertiesDetails[],
    userProperties : UserPropertiesDetails[]
}

const emptyAvailableProperties : AvailableProperties = {globalProperties : [], domainProperties : [], userProperties : []}

export interface IAvailablePropertiesContext {
    availablePropertiesLoading: boolean,
    availableProperties : AvailableProperties,
    clearAvailableProperties: () => void,
}

export const AvailablePropertiesContext = createContext<IAvailablePropertiesContext>({
    availablePropertiesLoading: true,
    availableProperties: emptyAvailableProperties,
    clearAvailableProperties: () => {},
});

interface AvailablePropertiesContextProviderProps {
    children: React.ReactNode;
}

function filterPropertyDescriptorDetails(props: UserPropertyDescriptorDetails[], level: string)
{
    const newProps: UserPropertyDescriptorDetails[] = []

    props.forEach((prop : UserPropertyDescriptorDetails) => {
        if (prop.getter[level as keyof typeof prop.getter]) {
            newProps.push(prop)
        }
    })
    return newProps
}

function sortAvailableProperties(availableProperties : UserPropertiesDetails[])
{
    // sort the UserPropertiesDetails
    availableProperties.sort((upd1, upd2) => (upd1.order - upd2.order))

    availableProperties.forEach((propertyDetails) =>
    {
        // depth first recursive sort
        if (propertyDetails.children.length > 0) {
            sortAvailableProperties(propertyDetails.children);
        }

        propertyDetails.properties.sort(
            (descriptor1: {getter: {order: number}}, descriptor2: {getter: { order: number }}) => (
                descriptor1.getter.order - descriptor2.getter.order)
        );
    })
}

type UserPropertyDetailsMatcher = (userPropertyDetails : UserPropertyDetails) => boolean;

/*
 * Do a depth first recursive pruning of properties which do no match. Also remove parents which only have empty
 * children
 */
function pruneProperties(availableProperties : UserPropertiesDetails[], matcher: UserPropertyDetailsMatcher) : UserPropertiesDetails[]
{
    const result : UserPropertiesDetails[] = [];

    availableProperties.forEach((propertyDetails) =>
    {
        // since we are updating an object, we need to clone
        const clone = {...propertyDetails};

        let children : UserPropertiesDetails[] = [];

        if (clone.children.length > 0) {
            children = pruneProperties(clone.children, matcher);
        }

        clone.properties = clone.properties.filter(pd => matcher(pd.getter));

        if (children.length > 0 || clone.properties.length > 0)
        {
            clone.children = children;
            result.push(clone);
        }
    })

    return result;
}

/**
 * A context provider which retrieves all available properties from the back-end and split them into
 * system (global), domain and user level available properties.
 *
 * Usage: wrap components within a AvailablePropertiesContextProvider
 *
 *  <AvailablePropertiesContextProvider>
 *      <AuthenticatedRoute><AdminLayout/></AuthenticatedRoute>
 *  </AvailablePropertiesContextProvider>
 *
 * Consumers of the available properties should use a useAuthenticatedUserContext hook
 *
 * @param children
 * @constructor
 */
export function AvailablePropertiesContextProvider({children} : Readonly<AvailablePropertiesContextProviderProps>)
{
    const [availablePropertiesLoading, setAvailablePropertiesLoading] = useState(true);
    const [availableProperties, setAvailableProperties] = useState<AvailableProperties>(emptyAvailableProperties)
    /**
     * Keeps track of activities for which the spinner should be shown. If an activity is started, the counter should
     * be increased by one and after the activity
     */
    const {setSpinnerStateCounter} = useSpinnerStatusContext();

    function clearAvailableProperties() {
        setAvailableProperties(emptyAvailableProperties);
    }

    const loadAvailableProperties = useCallback(() =>
    {
        const propertyApi = new PropertyApi(ApiConfig.config())

        // Load available properties and split them into user, domain and global properties and sort the properties
        propertyApi.propertyControllerGetAvailableProperties().then((response) =>
        {
            logger.info("Loading available properties")

            setAvailablePropertiesLoading(true);

            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count + 1);
            }

            const allAvailableProperties : UserPropertiesDetails[] = response.data;

            let globalProperties: UserPropertiesDetails[] = []
            let domainProperties: UserPropertiesDetails[] = []
            let userProperties: UserPropertiesDetails[] = []

            // split all available properties into global, domain and user
            allAvailableProperties.forEach((propertyDetails : UserPropertiesDetails) =>
            {
                globalProperties.push({
                    ...propertyDetails,
                    properties: filterPropertyDescriptorDetails(propertyDetails.properties, 'global')
                })
                domainProperties.push({
                    ...propertyDetails,
                    properties: filterPropertyDescriptorDetails(propertyDetails.properties, 'domain')
                })
                userProperties.push({
                    ...propertyDetails,
                    properties: filterPropertyDescriptorDetails(propertyDetails.properties, 'user')
                })
            })

            // // remove all non-global properties and empty parents
            globalProperties = pruneProperties(globalProperties, (userPropertyDetails : UserPropertyDetails) => userPropertyDetails.global);

            // remove all non-domain properties and empty parents
            domainProperties = pruneProperties(domainProperties, (userPropertyDetails : UserPropertyDetails) => userPropertyDetails.domain);

            // remove all non-user properties and empty parents
            userProperties = pruneProperties(userProperties, (userPropertyDetails : UserPropertyDetails) => userPropertyDetails.user);

            sortAvailableProperties(globalProperties);
            sortAvailableProperties(domainProperties);
            sortAvailableProperties(userProperties);

            setAvailableProperties({globalProperties : globalProperties, userProperties : userProperties, domainProperties : domainProperties})
        }).catch((err: Error | AxiosError) => {
            logger.error("Error loading available properties", err);
            setAvailableProperties(emptyAvailableProperties);
            // NOTE: do not add navigate to login because that would result in loading the available properties
            // everytime the location changes
        }).finally(() => {
            setAvailablePropertiesLoading(false);
            if (setSpinnerStateCounter !== undefined) {
                setSpinnerStateCounter(count => count - 1);
            }
        })
    }, [setAvailableProperties, setSpinnerStateCounter])

    useEffect(() => {
        loadAvailableProperties();
    }, [loadAvailableProperties]);

    const exposedData = useMemo<IAvailablePropertiesContext>(() => {
        const availablePropertiesContext : IAvailablePropertiesContext =
            {
                availablePropertiesLoading,
                availableProperties,
                clearAvailableProperties
            }

            return availablePropertiesContext;
    }, [availableProperties,
        availablePropertiesLoading]);

    return(
        <AvailablePropertiesContext.Provider value = {exposedData}>
            {children}
        </AvailablePropertiesContext.Provider>
    )
}
